import { FC } from 'react';
import { ResponsiveChord } from '@nivo/chord';

import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { ReportComponent } from '@/types';

import { humanizeChartLabel, truncateChartLabel } from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { useNivoReportData } from '../useNivoReportData';
import { useNivoReportFormatting } from '../useNivoReportFormatting';

const fallbackData = {
    keys: ['John', 'Raoul', 'Jane', 'Marcel', 'Ibrahim'],
    data: [
        [720, 330, 121, 298, 1901],
        [175, 462, 1979, 112, 985],
        [190, 963, 30, 44, 312],
        [255, 541, 304, 199, 241],
        [206, 15, 85, 278, 481],
    ],
};

interface ChordChartProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

const ChordChart: FC<ChordChartProps> = ({ component, numerator, denominator }): JSX.Element => {
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();
    const chartData = useNivoReportData(component, fallbackData);
    const { formatMetricValue } = useNivoReportFormatting(component);

    return (
        <div
            style={{
                aspectRatio: `${numerator}/${denominator}`,
                height: '100%',
                minHeight: 0,
                width: '100%',
            }}
        >
            <ResponsiveChord
                data={chartData.data}
                theme={nivoChartTheme}
                colors={colorsArray}
                keys={chartData.keys}
                margin={{ top: 48, right: 96, bottom: 48, left: 96 }}
                valueFormat={(value) => formatMetricValue(value)}
                padAngle={0.02}
                innerRadiusRatio={0.96}
                innerRadiusOffset={0.02}
                inactiveArcOpacity={0.25}
                arcBorderColor={{
                    from: 'color',
                    modifiers: [['darker', 0.6]],
                }}
                activeRibbonOpacity={0.75}
                inactiveRibbonOpacity={0.25}
                ribbonBorderColor={{
                    from: 'color',
                    modifiers: [['darker', 0.6]],
                }}
                label={(arc) => truncateChartLabel(arc.id, 12)}
                labelRotation={-90}
                labelTextColor={nivoChartTheme.textColor as string}
                motionConfig='gentle'
                arcTooltip={({ arc }) => (
                    <CustomNivoChartTooltip
                        title={humanizeChartLabel(arc.label)}
                        entries={[
                            {
                                color: arc.color,
                                label: 'Total',
                                value: arc.value,
                            },
                        ]}
                        formatValue={formatMetricValue}
                    />
                )}
                ribbonTooltip={({ ribbon }) => (
                    <CustomNivoChartTooltip
                        title={`${humanizeChartLabel(ribbon.source.label)} -> ${humanizeChartLabel(ribbon.target.label)}`}
                        entries={[
                            {
                                color: ribbon.source.color,
                                label: ribbon.source.label,
                                value: ribbon.source.value,
                            },
                            {
                                color: ribbon.target.color,
                                label: ribbon.target.label,
                                value: ribbon.target.value,
                            },
                        ]}
                        formatValue={formatMetricValue}
                    />
                )}
                legends={[]}
            />
        </div>
    );
};

export default ChordChart;
